import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useGetImportMetadata } from 'lib/hooks/queries/practice/useGetImportMetadata';

import PatientCSVImport from 'components/admin/pages/Practices/PatientImport/components/PatientCSVImport';
import PastImportsModal from 'components/admin/pages/Practices/PatientImport/components/PastImports/PastImportsModal';

import { WIZARD_PROGRESS } from '../../lib/enum';
import SubstepConfirmationModal from '../SubstepConfirmationModal/SubstepConfirmationModal';

import SoftwareExport from './components/SoftwareExport';
import { useImportActions } from './lib/useImportActions';
import ImportTypeSelector from './components/ImportTypeSelector';
import { useImportForm, useImportModals } from './lib/useImportForm';
import ProcessingFilesPlaceholder from './ProcessingFilesPlaceholder';
import ImportOverviewWrapper from './components/ImportOverviewWrapper';

const OnboardingImports = (props) => {
  const { practiceId } = useOutletContext();
  const form = useImportForm();
  const modals = useImportModals();
  const { defaultActions } = useImportActions({ props, modals, form });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { addFooterActions, setHeaderButtonProps } = props;

  const { data } = useGetImportMetadata({
    params: {
      practice_id: practiceId
    }
  });

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      ...defaultActions,
      ...(data?.imports?.length === 0
        ? [
            {
              text: 'Continue',
              onClick: () => setShowConfirmModal(true)
            }
          ]
        : [
            {
              text: 'Confirm patients',
              onClick: () =>
                props.mutateOrchestratorData({ importIds: data?.imports?.map((i) => i.id) }),
              iconRight: 'arrow-corner-down-left-linear',
              iconColor: 'white',
              disabled: props.orchestratorData.handlerStatus === WIZARD_PROGRESS.COMPLETED
            }
          ])
    ]);
  }, [defaultActions, data?.imports?.length, props.orchestratorData.handlerStatus]);

  const handleFinishMapping = () => {
    modals.setShowProcessingFiles(true);
    addFooterActions([
      {
        text: 'Import more',
        onClick: () => {
          modals.setShowProcessingFiles(false);
          addFooterActions(defaultActions);
        },
        outlined: true
      },
      {
        text: 'Continue',
        onClick: props.onNext,
        iconRight: 'chevron-right-linear',
        iconColor: 'white'
      }
    ]);
  };

  if (modals.showProcessingFiles) {
    return <ProcessingFilesPlaceholder />;
  }

  if (modals.showSoftwareExport) {
    return <SoftwareExport />;
  }

  if (modals.showImportOverview.isOpen) {
    return (
      <ImportOverviewWrapper
        overviewData={modals.showImportOverview.data}
        file={modals.showImportOverview.file}
      />
    );
  }

  return (
    <>
      <ImportTypeSelector
        initialImportForm={form.initialImportForm}
        handleImportTypeSelection={form.handleImportTypeSelection}
        handleChange={form.handleChange}
      />

      <PastImportsModal
        isOpen={!!modals.showPastImports}
        handleClose={() => modals.setShowPastImports(false)}
        onViewPastImport={(data) => {
          modals.setShowImportOverview({
            isOpen: true,
            data: {
              success: data?.counts?.imported,
              expected: data?.counts?.total,
              missingInfo: data?.counts?.missingInfo,
              failed: data?.counts?.errors
            },
            file: {
              fileName: data?.fileName,
              fileType: 'CSV',
              duration: '00:02:35'
            }
          });
          addFooterActions([
            {
              text: 'Import more',
              onClick: () => {
                modals.setShowImportOverview({ isOpen: false, data: null, file: null });
                addFooterActions(defaultActions);
              },
              outlined: true
            },
            {
              text: 'Continue',
              onClick: props.onNext,
              iconRight: 'chevron-right-linear',
              iconColor: 'white'
            }
          ]);
        }}
      />

      {modals.showImportCSV && (
        <PatientCSVImport
          onFinishMapping={handleFinishMapping}
          isOpen={modals.showImportCSV}
          handleClose={() => modals.setShowImportCSV(false)}
        />
      )}

      <SubstepConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onMarkComplete={() => {
          setShowConfirmModal(false);
          props.mutateOrchestratorData({ importIds: [], skipExternalSetup: true });
        }}
        onSkip={() => {
          setShowConfirmModal(false);
          props.onNext();
        }}
        title="No data imported"
        message="You haven't imported any patient data yet. Choose how you'd like to proceed before moving on."
        isLoading={props.orchestratorLoading}
      />
    </>
  );
};

export default OnboardingImports;
